// frontend/src/components/shared/AvailabilityWarning.tsx
import { useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { getSemesterDisplayName } from '../../utils/semesterUtils';
import type { NextOffering } from '../../hooks/useCourseData';

export interface AvailabilityWarningData {
  courseCode: string;
  courseTitle?: string;
  targetSemester: string;
  nextOffering?: NextOffering | null;
}

export interface AvailabilityWarningProps {
  warning: AvailabilityWarningData | null;
  onClose: () => void;
}

const AvailabilityWarning = ({ warning, onClose }: AvailabilityWarningProps) => {
  useEffect(() => {
    if (warning) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [warning, onClose]);

  if (!warning) return null;

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
      <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-lg p-4 max-w-md">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-amber-800">Course Availability Warning</h3>
            <p className="mt-1 text-sm text-amber-700">
              <span className="font-semibold">{warning.courseCode}</span>
              {warning.courseTitle && ` - ${warning.courseTitle}`}
            </p>
            <p className="mt-1 text-sm text-amber-600">
              This course is not typically offered in{' '}
              {warning.targetSemester && getSemesterDisplayName(warning.targetSemester)}.
              {warning.nextOffering?.display
                ? ` Next offering: ${warning.nextOffering.display}`
                : ''}
            </p>
            <p className="mt-2 text-xs text-amber-500">
              The course has been added to your plan, but please verify with the official
              schedule.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 flex-shrink-0 inline-flex text-amber-400 hover:text-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-amber-50 rounded"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityWarning;
